import React, { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  comparisonSummary,
  definitionActionStatuses,
  definitionsFor,
  type DefinitionAction,
  filtersChipText,
  formatDay,
  formatRange,
  notUsedNote,
  notUsedTitle,
  partValueText,
  periodSummary,
  screenLabel,
} from '../../lib/contextSummary';
import { isDateRange } from '../../lib/perfSettings';
import {
  resolveComparisonWindow,
  resolvePeriodWindow,
  screenCapabilities,
  unusedActiveParts,
  type ContextPart,
  type ScreenId,
  type ViewContextLink,
  type ViewContextState,
} from '../../lib/viewContext';
import type { ReservationStatusGroup } from '../../types';
import { Icon } from '../shared/Icon';
import { ContextOverlay } from './overlay';

export type VenueChipInfo = {
  /** "ButchersTable am Hegibachplatz", "All venues of ButchersTable", a group's name. */
  label: string;
  count: number;
  /** The venues' names, for the hover and focus card. */
  names: string[];
  /** Where they come from, in words: "Your group “Zürich”", "From a shared link". */
  origin: string;
  /** Growth's demo group: fictional venues, never the context's. */
  demo?: boolean;
  /** The demo group's local focus: one of its venues or brands (Phase C). */
  demoFocus?: DemoFocus | null;
};

/**
 * Growth's demo group can be read for one of its venues or brands: a local
 * focus like Live's outlet, chosen from the demo chip and shown as the
 * breadcrumb; the context's venues never change.
 */
export type DemoFocus = {
  options: { id: string; label: string; group?: 'brand' | 'venue' }[];
  value: string;
  onChange: (id: string) => void;
};

/** A screen's own window: Forecast's outlook ("Next 30 days"), Live's today. */
export type OwnWindow =
  | {
      kind: 'forecast';
      label: string;
      start: string | null;
      end: string | null;
      options: { key: string; label: string }[];
      value: string;
      onChange: (key: string) => void;
    }
  | { kind: 'live'; date: string };

/** A local drill-down inside the context that never changes it (audit B4). */
export type FocusCrumb = { key: string; label: string; onClear: () => void };

export type LinkNotice = {
  facts: ViewContextLink | null;
  onKeep: () => void;
  onBack: () => void;
};

export type ComparingInfo = { count: number; supported: boolean };

const MAX_NAMES = 12;

/**
 * The context bar (Phase B step 3): one row of chips that states, in words
 * and with dates, what every figure on the screen counts: the venues, the
 * period (or the screen's own window), the comparison and the reservation
 * filters, plus how the screen counts. A part the screen doesn't use but
 * that is set stays visible, muted, "not used on Forecast" (or the place,
 * "not used while connecting Google", notUsedNote). Every chip opens
 * the one Filters panel on its own part.
 */
export function ContextBar({
  screen,
  state,
  venues,
  comparing,
  ownWindow,
  focus = [],
  link,
  narrow,
  onOpen,
  barRef,
  tools,
  onSetStatuses,
}: {
  screen: ScreenId;
  state: ViewContextState;
  venues: VenueChipInfo;
  comparing: ComparingInfo | null;
  ownWindow?: OwnWindow | null;
  focus?: FocusCrumb[];
  link?: LinkNotice | null;
  narrow: boolean;
  onOpen: (part: ContextPart) => void;
  barRef?: React.Ref<HTMLDivElement>;
  /** On a phone the topbar's tools share the venues' row, so the summary below gets the full width. */
  tools?: React.ReactNode;
  /**
   * Applies statuses from the definitions popover's buttons, through the
   * same path as the Filters panel's Apply (URL, saved context, bar).
   */
  onSetStatuses?: (statuses: ReservationStatusGroup[]) => void;
}) {
  const { t } = useTranslation('chrome');
  if (screen === 'growth:accounts') {
    // "Your Google accounts" is outside any restaurant's context: one
    // static chip, no venues, period or filters (docs/19 section 5).
    return (
      <div className={`cx-bar${narrow ? ' is-narrow' : ''}`} ref={barRef} role="group" aria-label={t('context.barAria')}>
        <div className="cx-row">
          <span className="cx-chip is-static" data-cx-static="accounts">
            <span className="cx-chip-val">{t('growth:accountConnect.chip')}</span>
          </span>
          {narrow ? tools : null}
        </div>
      </div>
    );
  }
  const uses = screenCapabilities(screen).uses;
  const screenName = screenLabel(screen);
  const period = resolvePeriodWindow(state.perfSettings);
  const periodText = periodSummary(period, state.perfSettings);
  const comparison = comparisonSummary(resolveComparisonWindow(state.perfSettings, period), state.perfSettings);
  const filters = filtersChipText(screen, state);
  const definitions = definitionsFor(screen, state);
  const unused = unusedActiveParts(screen, state).filter(part => part !== 'venues');
  // Forecast has its own window: the period set elsewhere shows there, muted, always (spec 7.2).
  if (screen === 'forecast' && !unused.includes('period')) unused.unshift('period');
  const periodValue = `${periodText.range}${periodText.inProgress ? ` · ${t('context.period.inProgress')}` : ''}`;
  const comparisonValue = comparison.none ? comparison.text : `${comparison.text} · ${comparison.kind}`;
  const venueValue = comparing?.supported
    ? t('context.venues.comparing', { count: comparing.count })
    : venues.count > 1
      ? `${venues.label} · ${t('context.venues.count', { count: venues.count })}`
      : venues.label;

  const unusedValue = (part: Exclude<ContextPart, 'venues'>) =>
    part === 'period'
      ? isDateRange(state.perfSettings)
        ? periodText.range
        : periodText.name
      : partValueText(part, {
          status: state.status,
          source: state.source,
          shifts: state.shifts,
          comparison,
        });

  const unusedChips = unused.map(part => {
    const value = unusedValue(part as Exclude<ContextPart, 'venues'>);
    const chipText =
      part === 'period'
        ? t('context.period.chip', { value })
        : part === 'comparison'
          ? t('context.comparison.chip', { value })
          : value;
    return (
      <button
        key={`unused-${part}`}
        type="button"
        className="cx-chip is-unused"
        onClick={() => onOpen(part)}
        title={notUsedTitle(screen, chipText)}
        data-cx-unused={part}
      >
        <span className="cx-chip-val">{chipText}</span>
        <span className="cx-chip-note">{` · ${notUsedNote(screen)}`}</span>
      </button>
    );
  });

  const comparingUnused =
    comparing && !comparing.supported ? (
      <button
        key="unused-compare"
        type="button"
        className="cx-chip is-unused"
        onClick={() => onOpen('venues')}
        data-cx-unused="compare"
      >
        <span className="cx-chip-val">{t('context.venues.comparingNotUsed', { count: comparing.count })}</span>
        <span className="cx-chip-note">{` · ${notUsedNote(screen)}`}</span>
      </button>
    ) : null;

  const venueChip = (
    <VenueChip
      info={venues}
      value={venueValue}
      comparing={!!comparing?.supported}
      onOpen={() => onOpen('venues')}
      className={narrow ? 'is-wide' : undefined}
    />
  );

  const ownChip = ownWindow ? <OwnWindowChip own={ownWindow} /> : null;

  const crumbs = focus.length ? (
    <nav className="cx-crumbs" aria-label={t('context.focus.aria')}>
      {focus.map(crumb => (
        <span key={crumb.key} className="cx-crumb">
          <Icon name="chevronRight" size={12} className="cx-crumb-sep" />
          <span className="cx-crumb-name">{crumb.label}</span>
          <button
            type="button"
            className="cx-crumb-x"
            onClick={crumb.onClear}
            aria-label={t('context.focus.clear', { name: crumb.label })}
            title={t('context.focus.clear', { name: crumb.label })}
          >
            <Icon name="close" size={11} />
          </button>
        </span>
      ))}
    </nav>
  ) : null;

  const definitionsChip = (
    <DefinitionsChip screenName={screenName} definitions={definitions} compact={narrow} onSetStatuses={onSetStatuses} />
  );

  const notice = link ? <LinkNoticeRow link={link} /> : null;

  if (narrow) {
    // A phone: the venues, then one summary of the rest that opens the sheet.
    const lines = [uses.period ? periodValue : null, uses.comparison ? comparisonValue : null, filters].filter(
      Boolean,
    ) as string[];
    return (
      <div className="cx-bar is-narrow" ref={barRef} role="group" aria-label={t('context.barAria')}>
        <div className="cx-row">
          {venueChip}
          {definitionsChip}
          {tools}
        </div>
        {ownWindow && !uses.period ? <div className="cx-row">{ownChip}</div> : null}
        {lines.length ? (
          <div className="cx-row">
            {lines.length ? (
              <button
                type="button"
                className="cx-summary"
                data-ftue="chrome-period"
                onClick={() => onOpen(uses.period ? 'period' : uses.comparison ? 'comparison' : 'statuses')}
                aria-label={`${t('context.panel.title')}: ${lines.join('. ')}`}
              >
                <span className="cx-summary-lines">
                  {lines.map(line => (
                    <span key={line} className="cx-summary-line">
                      {line}
                    </span>
                  ))}
                </span>
                <Icon name="chevron" size={12} className="cx-chev" />
              </button>
            ) : null}
          </div>
        ) : null}
        {unusedChips.length || comparingUnused || crumbs ? (
          <div className="cx-row is-wrap">
            {crumbs}
            {comparingUnused}
            {unusedChips}
          </div>
        ) : null}
        {notice}
      </div>
    );
  }

  return (
    <div className="cx-bar" ref={barRef} role="group" aria-label={t('context.barAria')}>
      <div className="cx-row is-wrap">
        {/* Which venues and when, then what counts: each cluster wraps as one. */}
        <span className="cx-cluster">
        {venueChip}
        {crumbs}
        {ownChip}
        {uses.period ? (
          <button
            type="button"
            className="cx-chip"
            data-ftue="chrome-period"
            onClick={() => onOpen('period')}
            title={`${periodText.name}: ${periodText.range}`}
            aria-label={t('context.period.aria', { value: `${periodText.name}, ${periodValue}` })}
          >
            <Icon name="calendar" size={13} className="cx-chip-ic" />
            <span className="cx-chip-val">{periodValue}</span>
            <Icon name="chevron" size={11} className="cx-chev" />
          </button>
        ) : null}
        {uses.comparison ? (
          <button
            type="button"
            className="cx-chip"
            onClick={() => onOpen('comparison')}
            aria-label={t('context.comparison.aria', { value: comparisonValue })}
          >
            <span className="cx-chip-val">
              {comparison.text}
              {comparison.kind ? <span className="cx-chip-kind">{` · ${comparison.kind}`}</span> : null}
            </span>
            <Icon name="chevron" size={11} className="cx-chev" />
          </button>
        ) : null}
        </span>
        <span className="cx-cluster">
        {filters ? (
          <button
            type="button"
            className={`cx-chip${state.status.length || state.source.length || state.shifts.length ? ' is-set' : ''}`}
            data-ftue="chrome-status"
            onClick={() => onOpen(uses.statuses ? 'statuses' : uses.sources ? 'sources' : 'shifts')}
            aria-label={t('context.filters.aria', { value: filters })}
          >
            <Icon name="sliders" size={13} className="cx-chip-ic" />
            <span className="cx-chip-val">{filters}</span>
            <Icon name="chevron" size={11} className="cx-chev" />
          </button>
        ) : null}
        {comparingUnused}
        {unusedChips}
        {definitionsChip}
        </span>
      </div>
      {notice}
    </div>
  );
}

function VenueChip({
  info,
  value,
  comparing,
  onOpen,
  className,
}: {
  info: VenueChipInfo;
  value: string;
  comparing: boolean;
  onOpen: () => void;
  className?: string;
}) {
  const { t } = useTranslation('chrome');
  const [card, setCard] = useState(false);
  const cardId = useId();
  const names = info.names.slice(0, MAX_NAMES);
  const rest = info.names.length - names.length;
  const cardBody = (
    <span className="cx-card" role="tooltip" id={cardId} hidden={!card}>
      <span className="cx-card-head">{info.origin}</span>
      {names.length ? (
        <span className="cx-card-names">
          {names.map(name => (
            <span key={name}>{name}</span>
          ))}
          {rest > 0 ? <span className="cx-card-more">{t('context.venues.andMore', { count: rest })}</span> : null}
        </span>
      ) : null}
    </span>
  );
  if (info.demo) {
    if (info.demoFocus && info.demoFocus.options.length > 1) {
      return <DemoFocusChip label={info.label} focus={info.demoFocus} className={className} />;
    }
    return (
      <span className={`cx-chip is-static${className ? ` ${className}` : ''}`} title={t('context.venues.demoTitle')}>
        <Icon name="utensils" size={13} className="cx-chip-ic" />
        <span className="cx-chip-val">{info.label}</span>
      </span>
    );
  }
  return (
    <span
      className={`cx-chip-wrap${className ? ` ${className}` : ''}`}
      onMouseEnter={() => setCard(true)}
      onMouseLeave={() => setCard(false)}
    >
      <button
        type="button"
        className="cx-chip is-venues"
        data-ftue="chrome-scope"
        onClick={() => {
          setCard(false);
          onOpen();
        }}
        onFocus={() => setCard(true)}
        onBlur={() => setCard(false)}
        onKeyDown={e => {
          if (e.key === 'Escape') setCard(false);
        }}
        aria-describedby={cardId}
        aria-label={t('context.venues.aria', { value })}
      >
        <Icon name={comparing ? 'grid' : 'utensils'} size={13} className="cx-chip-ic" />
        <span className="cx-chip-val cx-venue-name">{value}</span>
        <Icon name="chevron" size={11} className="cx-chev" />
      </button>
      {cardBody}
    </span>
  );
}

/** The demo group's chip: its name, and a menu to focus on one of its venues or brands. */
function DemoFocusChip({ label, focus, className }: { label: string; focus: DemoFocus; className?: string }) {
  const { t } = useTranslation('chrome');
  const ref = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const menuId = 'cx-demo-focus';
  const groups: { key: 'all' | 'brand' | 'venue'; title: string | null }[] = [
    { key: 'all', title: null },
    { key: 'brand', title: t('context.focus.demoBrands') },
    { key: 'venue', title: t('context.focus.demoVenues') },
  ];
  return (
    <>
      <button
        ref={ref}
        type="button"
        className={`cx-chip is-venues${className ? ` ${className}` : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={t('context.venues.demoTitle')}
        aria-label={t('context.focus.demoAria', { value: label })}
        onClick={() => setOpen(v => !v)}
        data-cx-demo-focus
      >
        <Icon name="utensils" size={13} className="cx-chip-ic" />
        <span className="cx-chip-val cx-venue-name">{label}</span>
        <Icon name="chevron" size={11} className="cx-chev" />
      </button>
      <ContextOverlay open={open} anchorRef={ref} onClose={() => setOpen(false)} width={300} className="cx-menu" labelledBy={menuId}>
        <h2 className="cx-menu-title" id={menuId}>
          {t('context.focus.demoMenu')}
        </h2>
        <p className="cx-menu-note">{t('context.focus.demoNote')}</p>
        <div className="cx-list" role="radiogroup" aria-labelledby={menuId}>
          {groups.map(group => {
            const options = focus.options.filter(option => (option.group ?? 'all') === group.key);
            if (!options.length) return null;
            return (
              <React.Fragment key={group.key}>
                {group.title ? <span className="cx-list-head">{group.title}</span> : null}
                {options.map(option => (
                  <label key={option.id} className={`cx-opt${option.id === focus.value ? ' is-on' : ''}`}>
                    <input
                      type="radio"
                      name="cx-demo-focus"
                      checked={option.id === focus.value}
                      onChange={() => {
                        focus.onChange(option.id);
                        setOpen(false);
                      }}
                    />
                    <span className="cx-opt-main">
                      <span className="cx-opt-name">{option.label}</span>
                    </span>
                  </label>
                ))}
              </React.Fragment>
            );
          })}
        </div>
      </ContextOverlay>
    </>
  );
}

function OwnWindowChip({ own }: { own: OwnWindow }) {
  const { t } = useTranslation('chrome');
  const ref = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  if (own.kind === 'live') {
    const value = t('context.own.live', { date: formatDay(own.date, { weekday: true, year: false }) });
    return (
      <span className="cx-chip is-static is-own" title={t('context.own.liveTitle')} data-cx-own="live">
        <Icon name="clock" size={13} className="cx-chip-ic" />
        <span className="cx-chip-val">{value}</span>
      </span>
    );
  }
  const range = own.start && own.end ? formatRange(own.start, own.end) : '';
  const value = range ? t('context.own.forecast', { label: own.label, range }) : own.label;
  const menuId = 'cx-forecast-window';
  return (
    <>
      <button
        ref={ref}
        type="button"
        className="cx-chip is-own"
        data-cx-own="forecast"
        aria-haspopup="dialog"
        aria-expanded={open}
        title={t('context.own.forecastTitle')}
        aria-label={t('context.own.forecastAria', { value })}
        onClick={() => setOpen(v => !v)}
      >
        <Icon name="calendar" size={13} className="cx-chip-ic" />
        <span className="cx-chip-val">{value}</span>
        <Icon name="chevron" size={11} className="cx-chev" />
      </button>
      <ContextOverlay
        open={open}
        anchorRef={ref}
        onClose={() => setOpen(false)}
        width={280}
        className="cx-menu"
        labelledBy={menuId}
      >
        <h2 className="cx-menu-title" id={menuId}>
          {t('context.own.forecastMenu')}
        </h2>
        <p className="cx-menu-note">{t('context.own.forecastTitle')}</p>
        <div className="cx-list" role="radiogroup" aria-labelledby={menuId}>
          {own.options.map(option => (
            <label key={option.key} className={`cx-opt${option.key === own.value ? ' is-on' : ''}`}>
              <input
                type="radio"
                name="cx-forecast-window"
                checked={option.key === own.value}
                onChange={() => {
                  own.onChange(option.key);
                  setOpen(false);
                }}
              />
              <span className="cx-opt-main">
                <span className="cx-opt-name">{option.label}</span>
              </span>
            </label>
          ))}
        </div>
      </ContextOverlay>
    </>
  );
}

function DefinitionsChip({
  screenName,
  definitions,
  compact,
  onSetStatuses,
}: {
  screenName: string;
  definitions: { chip: string; title: string; lines: string[]; actions?: DefinitionAction[] };
  compact: boolean;
  onSetStatuses?: (statuses: ReservationStatusGroup[]) => void;
}) {
  const { t } = useTranslation('chrome');
  const ref = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const titleId = 'cx-definitions-title';
  return (
    <>
      <button
        ref={ref}
        type="button"
        className={`cx-defs${compact ? ' is-compact' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${definitions.chip}. ${t('context.definitions.open', { screen: screenName })}`}
        onClick={() => setOpen(v => !v)}
        data-cx-definitions
      >
        {compact ? null : <span className="cx-defs-text">{definitions.chip}</span>}
        <Icon name="info" size={13} />
      </button>
      <ContextOverlay
        open={open}
        anchorRef={ref}
        onClose={() => setOpen(false)}
        width={360}
        align="end"
        className="cx-menu cx-defs-pop"
        labelledBy={titleId}
      >
        <div className="cx-menu-head">
          <h2 className="cx-menu-title" id={titleId}>
            {definitions.title}
          </h2>
          <button type="button" className="cx-icon-btn" onClick={() => setOpen(false)} aria-label={t('context.panel.close')}>
            <Icon name="close" size={14} />
          </button>
        </div>
        {compact ? <p className="cx-defs-chip">{definitions.chip}</p> : null}
        {definitions.lines.map(line => (
          <p key={line} className="cx-menu-line">
            {line}
          </p>
        ))}
        {onSetStatuses && definitions.actions?.length ? (
          <div className="cx-defs-actions">
            {definitions.actions.map(action => (
              <button
                key={action}
                type="button"
                className="cx-btn is-small"
                data-cx-def-action={action}
                onClick={() => {
                  onSetStatuses(definitionActionStatuses(action));
                  setOpen(false);
                  ref.current?.focus();
                }}
              >
                {t('context.definitions.performance.countEvery')}
              </button>
            ))}
          </div>
        ) : null}
      </ContextOverlay>
    </>
  );
}

function LinkNoticeRow({ link }: { link: LinkNotice }) {
  const { t } = useTranslation('chrome');
  const facts = link.facts;
  const dropped = facts?.dropped.length ?? 0;
  return (
    <div className="cx-link-notice" role="status" aria-label={t('context.link.aria')}>
      <Icon name="link" size={13} className="cx-link-ic" />
      <p>
        <strong>{t('context.link.lead')}</strong> {t('context.link.shown')}
        {dropped ? ` ${t('context.link.dropped', { count: dropped, total: facts!.requested.length })}` : ''}
        {facts?.groupNotYours ? ` ${t('context.link.groupNotYours')}` : ''}
      </p>
      <span className="cx-link-actions">
        <button type="button" className="cx-btn is-small" onClick={link.onKeep}>
          {t('context.link.keep')}
        </button>
        <button type="button" className="cx-btn is-small is-quiet" onClick={link.onBack}>
          {t('context.link.back')}
        </button>
      </span>
    </div>
  );
}
